import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiEdit2, FiPlus, FiShield, FiStar, FiTrash2 } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import MoreMenu from './MoreMenu';
import {
  TAX_ID_TYPE_OPTIONS,
  TAX_REGIME_OPTIONS,
  defaultTaxIdTypeForCountry,
} from '../utils/taxDocumentFields';
import { appAlert, appConfirm } from '../utils/appDialog';

const EMPTY_FORM = {
  id: null,
  label: '',
  legal_name: '',
  trade_name: '',
  address_lines: '',
  country_code: 'US',
  state_province: '',
  default_tax_regime: 'us_sales',
  tax_compliance_mode: 'manual',
  default_sac_hsn: '',
  tax_ids: [{ type: 'ein', value: '' }],
  invoice_footer_notes:
    'This document is issued for commercial / process records. Payroll and tax returns are handled in separate systems.',
  payout_document_disclaimer:
    'This PAY document is an earnings statement for process records only. It is not a payroll remittance, bank payout confirmation, or statutory tax slip (1099 / T4A / Form 16A). Payroll and tax filing are handled in external systems.',
  is_default: false,
};

function profileToForm(p, { isDefault = false } = {}) {
  return {
    id: p.id || null,
    label: p.label || '',
    legal_name: p.legal_name || '',
    trade_name: p.trade_name || '',
    address_lines: p.address_lines || '',
    country_code: p.country_code || 'US',
    state_province: p.state_province || '',
    default_tax_regime: p.default_tax_regime || 'us_sales',
    tax_compliance_mode: p.tax_compliance_mode || 'manual',
    default_sac_hsn: p.default_sac_hsn || '',
    tax_ids:
      Array.isArray(p.tax_ids) && p.tax_ids.length
        ? p.tax_ids.map((t) => ({ type: t.type || 'other', value: t.value || '' }))
        : [{ type: defaultTaxIdTypeForCountry(p.country_code), value: '' }],
    invoice_footer_notes: p.invoice_footer_notes || '',
    payout_document_disclaimer: p.payout_document_disclaimer || '',
    is_default: isDefault,
  };
}

/**
 * Settings → Organization tax profiles for invoice / quotation documents.
 * List + create/edit. Does not file taxes or run payroll.
 */
export default function OrgTaxProfileSettings() {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState('list'); // list | create | edit
  const [form, setForm] = useState(EMPTY_FORM);

  const { data, isLoading, isFetching } = useQuery(['org-tax-profile'], () =>
    api.get('/services/finance/org-tax-profile').then((r) => r.data)
  );

  const profiles = data?.profiles || [];
  const defaultId = data?.default_id || null;

  const invalidate = () => queryClient.invalidateQueries(['org-tax-profile']);

  const saveMutation = useMutation(
    () =>
      api.put('/services/finance/org-tax-profile', {
        ...form,
        id: form.id || undefined,
        tax_ids: (form.tax_ids || []).filter((t) => String(t.value || '').trim()),
        is_default: Boolean(form.is_default) || profiles.length === 0,
      }),
    {
      onSuccess: (res) => {
        invalidate();
        queryClient.invalidateQueries(['sales-document']);
        queryClient.invalidateQueries(['service-sales-documents']);
        queryClient.invalidateQueries(['requisition']);
        setMode('list');
        setForm(EMPTY_FORM);
        const updated = Number(res?.data?.documents_updated) || 0;
        void appAlert(
          updated > 0
            ? `Tax profile saved. Updated seller details on ${updated} existing document${updated === 1 ? '' : 's'}. Regenerate PDFs to reprint the new GST details. New quotations and invoices use this profile too.`
            : 'Tax profile saved. New quotations and invoices use this seller profile.'
        );
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/services/finance/org-tax-profile/${encodeURIComponent(id)}`),
    {
      onSuccess: () => {
        invalidate();
        void appAlert('Tax profile deleted.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const defaultMutation = useMutation(
    (id) => api.put('/services/finance/org-tax-profile/default', { id }),
    {
      onSuccess: () => {
        invalidate();
        void appAlert('Default tax profile updated.');
      },
      onError: (e) => void appAlert(e.response?.data?.error || e.message),
    }
  );

  const openCreate = () => {
    setForm({
      ...EMPTY_FORM,
      tax_ids: [{ type: defaultTaxIdTypeForCountry('US'), value: '' }],
      is_default: profiles.length === 0,
    });
    setMode('create');
  };

  const openEdit = (profile) => {
    setForm(profileToForm(profile, { isDefault: profile.id === defaultId }));
    setMode('edit');
  };

  const cancelForm = () => {
    setForm(EMPTY_FORM);
    setMode('list');
  };

  const onDelete = async (profile) => {
    const ok = await appConfirm(
      `Delete tax profile “${profile.label || profile.legal_name || profile.id}”?`
    );
    if (!ok) return;
    deleteMutation.mutate(profile.id);
  };

  if (isLoading) {
    return <div className="loading">Loading organization tax profiles…</div>;
  }

  if (mode === 'create' || mode === 'edit') {
    return (
      <div className="settings-section">
        <h2>
          <FiShield /> {mode === 'edit' ? 'Edit tax profile' : 'New tax profile'}
        </h2>
        <p className="form-hint" style={{ marginTop: 0 }}>
          Seller details printed on quotations and invoices. Godash produces documents for process
          records only — payroll and tax filing stay in external systems.
        </p>
        {data?.scope_note ? <p className="form-hint">{data.scope_note}</p> : null}

        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label htmlFor="org-tax-label">Profile name</label>
            <input
              id="org-tax-label"
              value={form.label || ''}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="e.g. India GST · GoBunnyy IN"
            />
          </div>
          <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <label className="svc-check">
              <input
                type="checkbox"
                checked={Boolean(form.is_default)}
                onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
              />{' '}
              Use as default for new quotations and invoices
            </label>
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label htmlFor="org-tax-legal-name">Legal name *</label>
            <input
              id="org-tax-legal-name"
              value={form.legal_name || ''}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
            />
          </div>
          <div className="form-group">
            <label htmlFor="org-tax-trade-name">Trade name</label>
            <input
              id="org-tax-trade-name"
              value={form.trade_name || ''}
              onChange={(e) => setForm({ ...form, trade_name: e.target.value })}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="org-tax-address">Address</label>
          <textarea
            id="org-tax-address"
            rows={3}
            value={form.address_lines || ''}
            onChange={(e) => setForm({ ...form, address_lines: e.target.value })}
          />
        </div>

        <div className="svc-form-grid svc-form-grid--3">
          <div className="form-group">
            <label htmlFor="org-tax-country">Country</label>
            <select
              id="org-tax-country"
              value={form.country_code || 'US'}
              onChange={(e) => {
                const country_code = e.target.value;
                setForm({
                  ...form,
                  country_code,
                  default_tax_regime:
                    country_code === 'CA'
                      ? 'ca_gst'
                      : country_code === 'IN'
                        ? 'in_gst'
                        : country_code === 'US'
                          ? 'us_sales'
                          : form.default_tax_regime,
                });
              }}
            >
              <option value="US">United States</option>
              <option value="CA">Canada</option>
              <option value="IN">India</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="org-tax-state">State / province</label>
            <input
              id="org-tax-state"
              value={form.state_province || ''}
              onChange={(e) => setForm({ ...form, state_province: e.target.value.toUpperCase() })}
              placeholder="CA / ON / MH"
            />
          </div>
          <div className="form-group">
            <label htmlFor="org-tax-regime">Default tax regime</label>
            <select
              id="org-tax-regime"
              value={form.default_tax_regime || 'manual'}
              onChange={(e) => setForm({ ...form, default_tax_regime: e.target.value })}
            >
              {TAX_REGIME_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="svc-form-grid svc-form-grid--2">
          <div className="form-group">
            <label htmlFor="org-tax-compliance-mode">Compliance mode</label>
            <select
              id="org-tax-compliance-mode"
              value={form.tax_compliance_mode || 'manual'}
              onChange={(e) => setForm({ ...form, tax_compliance_mode: e.target.value })}
            >
              <option value="off">Off (no tax on documents)</option>
              <option value="manual">Manual / engine rates</option>
              <option value="engine">Engine (jurisdiction breakdown)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="org-tax-sac-hsn">Default SAC/HSN (India)</label>
            <input
              id="org-tax-sac-hsn"
              value={form.default_sac_hsn || ''}
              onChange={(e) => setForm({ ...form, default_sac_hsn: e.target.value })}
              placeholder="998311"
            />
          </div>
        </div>

        <h3 style={{ marginTop: '1.25rem' }}>Seller tax IDs</h3>
        {(form.tax_ids || []).map((row, idx) => (
          <div key={idx} className="svc-form-grid svc-form-grid--3" style={{ marginBottom: 8 }}>
            <div className="form-group">
              <label htmlFor={`org-tax-id-type-${idx}`}>Type</label>
              <select
                id={`org-tax-id-type-${idx}`}
                value={row.type || 'other'}
                onChange={(e) => {
                  const tax_ids = [...form.tax_ids];
                  tax_ids[idx] = { ...tax_ids[idx], type: e.target.value };
                  setForm({ ...form, tax_ids });
                }}
              >
                {TAX_ID_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor={`org-tax-id-value-${idx}`}>Value</label>
              <input
                id={`org-tax-id-value-${idx}`}
                value={row.value || ''}
                onChange={(e) => {
                  const tax_ids = [...form.tax_ids];
                  tax_ids[idx] = { ...tax_ids[idx], value: e.target.value };
                  setForm({ ...form, tax_ids });
                }}
              />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() =>
                  setForm({
                    ...form,
                    tax_ids: form.tax_ids.filter((_, i) => i !== idx),
                  })
                }
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() =>
            setForm({
              ...form,
              tax_ids: [
                ...(form.tax_ids || []),
                { type: defaultTaxIdTypeForCountry(form.country_code), value: '' },
              ],
            })
          }
        >
          Add tax ID
        </button>

        <div className="form-group" style={{ marginTop: '1rem' }}>
          <label htmlFor="org-tax-invoice-footer">Invoice footer / disclaimer</label>
          <textarea
            id="org-tax-invoice-footer"
            rows={3}
            value={form.invoice_footer_notes || ''}
            onChange={(e) => setForm({ ...form, invoice_footer_notes: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label htmlFor="org-tax-payout-disclaimer">PAY document disclaimer</label>
          <textarea
            id="org-tax-payout-disclaimer"
            rows={3}
            value={form.payout_document_disclaimer || ''}
            onChange={(e) => setForm({ ...form, payout_document_disclaimer: e.target.value })}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: '1rem' }}>
          <button type="button" className="btn btn-secondary" onClick={cancelForm}>
            Cancel
          </button>
          <LoadingButton
            className="btn btn-primary"
            loading={saveMutation.isLoading}
            onClick={() => saveMutation.mutate()}
          >
            {mode === 'edit' ? 'Save changes' : 'Create tax profile'}
          </LoadingButton>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-section">
      <h2>
        <FiShield /> Organization tax profiles
      </h2>
      <p className="form-hint" style={{ marginTop: 0 }}>
        Seller details printed on quotations and invoices. Saving a profile updates existing
        documents that use it, and new quotations and invoices pick it up immediately.
      </p>
      {data?.scope_note ? <p className="form-hint">{data.scope_note}</p> : null}

      <div style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          <FiPlus /> New tax profile
        </button>
      </div>

      {isFetching && profiles.length === 0 ? (
        <div className="loading">Refreshing…</div>
      ) : profiles.length === 0 ? (
        <p className="form-hint">
          No tax profiles yet. Create one to print seller tax IDs on invoices.
        </p>
      ) : (
        <div className="erp-table-scroll">
          <table className="table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th scope="col">Profile</th>
                <th scope="col">Country</th>
                <th scope="col">Status</th>
                <th scope="col" style={{ width: 56, textAlign: 'right' }}>
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p) => {
                const isDefault = p.id === defaultId;
                const name = p.label || p.legal_name || 'Untitled';
                return (
                  <tr key={p.id}>
                    <td>
                      <strong>{name}</strong>
                    </td>
                    <td>{p.country_code || '—'}</td>
                    <td>
                      {isDefault ? (
                        <span className="badge badge-info">Default</span>
                      ) : (
                        <span className="form-hint">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <MoreMenu
                        label={`Actions for ${name}`}
                        size="sm"
                        items={[
                          {
                            id: 'edit',
                            label: 'Edit',
                            icon: FiEdit2,
                            onSelect: () => openEdit(p),
                          },
                          {
                            id: 'make-default',
                            label: 'Make default',
                            icon: FiStar,
                            hidden: isDefault,
                            disabled: defaultMutation.isLoading,
                            onSelect: () => defaultMutation.mutate(p.id),
                          },
                          {
                            id: 'delete',
                            label: 'Delete',
                            icon: FiTrash2,
                            danger: true,
                            disabled: deleteMutation.isLoading,
                            onSelect: () => onDelete(p),
                          },
                        ]}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
